// src/hooks/useSwipeCard.js
import { useState, useCallback } from 'react';

/**
 * Hook for handling card swiping interaction
 * @param {Function} onSwipeLeft - Callback for when card is swiped left
 * @param {Function} onSwipeRight - Callback for when card is swiped right
 * @param {number} threshold - Distance threshold to trigger a swipe (px)
 * @returns {Object} Swipe handlers and state
 */
export const useSwipeCard = (onSwipeLeft, onSwipeRight, threshold = 100) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [animation, setAnimation] = useState(null);
  
  const handleStart = useCallback((e) => {
    setIsDragging(true);
    setStartX(e.type.includes('mouse') ? e.clientX : e.touches[0].clientX);
  }, []);

  const handleMove = useCallback((e) => {
    if (isDragging) {
      const currentX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      const diff = currentX - startX;
      setOffsetX(diff);
    }
  }, [isDragging, startX]);

  const handleEnd = useCallback(() => {
    if (isDragging) {
      if (offsetX > threshold) {
        // Swiped right
        setAnimation('swipe-right');
        setTimeout(() => {
          if (onSwipeRight) onSwipeRight();
          setAnimation(null);
        }, 300);
      } else if (offsetX < -threshold) {
        // Swiped left
        setAnimation('swipe-left');
        setTimeout(() => {
          if (onSwipeLeft) onSwipeLeft();
          setAnimation(null);
        }, 300);
      } else {
        // Reset if not swiped far enough
        setOffsetX(0);
      }
      setIsDragging(false);
    }
  }, [isDragging, offsetX, onSwipeLeft, onSwipeRight, threshold]);

  const resetOffset = useCallback(() => {
    setOffsetX(0);
  }, []);

  const triggerSwipeLeft = useCallback(() => {
    setAnimation('swipe-left');
    setTimeout(() => {
      if (onSwipeLeft) onSwipeLeft();
      setAnimation(null);
    }, 300);
  }, [onSwipeLeft]);

  const triggerSwipeRight = useCallback(() => {
    setAnimation('swipe-right');
    setTimeout(() => {
      if (onSwipeRight) onSwipeRight();
      setAnimation(null);
    }, 300);
  }, [onSwipeRight]);
  
  return {
    isDragging,
    offsetX,
    animation,
    handlers: {
      onMouseDown: handleStart,
      onMouseMove: handleMove,
      onMouseUp: handleEnd,
      onMouseLeave: handleEnd,
      onTouchStart: handleStart,
      onTouchMove: handleMove,
      onTouchEnd: handleEnd,
    },
    resetOffset,
    triggerSwipeLeft,
    triggerSwipeRight,
  };
};